import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  Grid,
  Chip,
  IconButton,
  Stack,
  Avatar,
  useTheme,
} from "@mui/material";
import { Save, X } from "lucide-react";
import { useEffect, useState } from "react";
import { allPermissions } from "../../config/allPermissions";

export default function RoleFormModal({
  open,
  onClose,
  role,
  currentUserRole,
  onSave,
}) {
  const [form, setForm] = useState({ name: "", permissions: [] });
  const isDisabled =
    role?.name === "Super Admin" || role?.name === currentUserRole;

  useEffect(() => {
    if (role)
      setForm({
        name: role.name,
        permissions: role.permissions,
      });
    else setForm({ name: "", permissions: [] });
  }, [role]);

  const parsePermissions = (permissions) => {
    const grouped = {};
    permissions.forEach((p) => {
      const [module, action] = p.split(".");
      if (!grouped[module]) grouped[module] = [];
      if (!grouped[module].includes(action)) grouped[module].push(action);
    });
    return grouped;
  };

  const groupedModules = parsePermissions(allPermissions);

  const togglePermission = (module, action) => {
    let updated = [...form.permissions];
    const key = `${module}.${action}`;

    if (updated.includes(key)) {
      updated = updated.filter((p) => p !== key);
    } else {
      updated.push(key);
    }

    const moduleActions = groupedModules[module]?.filter((a) => a !== "view");
    if (
      moduleActions.some((a) => updated.includes(`${module}.${a}`)) &&
      !updated.includes(`${module}.view`)
    ) {
      updated.push(`${module}.view`);
    }

    setForm({ ...form, permissions: updated });
  };

  const hasPermission = (module, action) =>
    form.permissions.includes(`${module}.${action}`);

  const formatText = (txt) => txt.charAt(0).toUpperCase() + txt.slice(1);

  const ACTIONS = ["view", "create", "update", "delete"];
  const ACTION_LABEL = {
    view: "View",
    create: "Create",
    update: "Update",
    delete: "Delete",
  };

  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          overflow: "visible",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: -1,
          right: -8,
          top: -1,
          height: 64,
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          display: "flex",
          alignItems: "center",
          px: 3,
          py: 2,
          color: "#000000ff",
          width: "94.8%",
        }}
      >
        <Avatar
          sx={{
            mr: 2,
            background: `linear-gradient(90deg, ${theme.avatarGradients_1.avatarStart} 0%, ${theme.avatarGradients_1.avatarCenter} 50%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
            color: "#fff",
            width: 44,
            height: 44,
            fontWeight: 800,
          }}
        >
          {role?.name ? role.name.charAt(0).toUpperCase() : "A"}
        </Avatar>

        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
            {role ? "Edit Role" : "Create Role"}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Give this role a name and choose permissions
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }} />

        <IconButton
          onClick={onClose}
          sx={{
            color: "rgba(0, 0, 0, 0.9)",
            bgcolor: "rgba(255,255,255,0.06)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          <X size={18} />
        </IconButton>
      </Box>

      <DialogTitle sx={{ pt: 6, pb: 0 }}></DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "flex-start",
            mb: 2,
            mt: 5,
          }}
        >
          <TextField
            label="Role Name"
            fullWidth
            disabled={isDisabled}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Product Manager"
            sx={{
              input: { fontWeight: 500 },
              "& .MuiInputLabel-root": { fontWeight: 500, fontSize: 14 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.02)",
              },
            }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {Object.keys(groupedModules).map((module) => {
            const moduleActions = groupedModules[module] || [];
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={module}>
                <Box
                  sx={{
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    p: 1.25,
                    minHeight: 110,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        color: "white",
                        background: `linear-gradient(90deg, ${theme.avatarGradients_1.avatarStart} 0%, ${theme.avatarGradients_1.avatarCenter} 50%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                        color: "#fff",
                        boxShadow: "0 6px 18px rgba(99,102,241,0.08)",
                      }}
                    >
                      {module.charAt(0).toUpperCase()}
                    </Box>

                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ textTransform: "capitalize", fontWeight: 600 }}
                      >
                        {formatText(module)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {moduleActions.length} actions
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1 }} />

                    <Chip
                      label={
                        moduleActions.every((a) => hasPermission(module, a))
                          ? "All"
                          : moduleActions.some((a) => hasPermission(module, a))
                          ? "Partial"
                          : "None"
                      }
                      size="small"
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 100,
                        borderRadius: 1,
                        fontSize: 12,
                        px: 0.5,
                        py: 1,
                        bgcolor: (() => {
                          if (
                            moduleActions.every((a) => hasPermission(module, a))
                          ) {
                            return "#A5D6A7";
                          }
                          if (
                            moduleActions.some((a) => hasPermission(module, a))
                          ) {
                            return "#C2E2FA";
                          }
                          return "neutral.200";
                        })(),
                        color: "black",
                      }}
                    />
                  </Box>

                  <Stack
                    direction="row"
                    gap={0.6}
                    flexWrap="wrap"
                    alignItems="center"
                  >
                    {ACTIONS.map((action) => {
                      const available = moduleActions.includes(action);
                      const active = hasPermission(module, action);

                      return (
                        <Chip
                          key={action}
                          label={ACTION_LABEL[action]}
                          clickable={available && !isDisabled}
                          onClick={() =>
                            available &&
                            !isDisabled &&
                            togglePermission(module, action)
                          }
                          variant={active ? "filled" : "outlined"}
                          sx={{
                            minWidth: 90,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            fontWeight: 400,
                            fontSize: 13,
                            textAlign: "center",
                            textTransform: "capitalize",

                            borderRadius: 2,
                            cursor: available ? "pointer" : "not-allowed",
                            opacity: available ? 1 : 0.45,
                            borderWidth: 1,

                            bgcolor: active
                              ? theme.actionStyles_form_bg_active[action]
                              : "transparent",

                            color: active
                              ? theme.actionStyles_form_color_active[action]
                              : "text.primary",

                            borderColor: active
                              ? theme.actionStyles_form_borderColor[action]
                              : "rgba(0,0,0,0.15)",

                            transition: "0.25s ease",
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "uppercase",
            fontWeight: 700,
            borderRadius: 2,
            color: "text.primary",
            bgcolor: "transparent",
            px: 2,
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={() => !isDisabled && onSave(form)}
          variant="contained"
          disabled={isDisabled}
          sx={{
            textTransform: "uppercase",
            fontWeight: 700,
            px: 3,
            borderRadius: 2,
            boxShadow: "0 8px 26px rgba(99,102,241,0.18)",
            background: `linear-gradient(90deg, ${theme.avatarGradients_1.avatarStart} 0%, ${theme.avatarGradients_1.avatarCenter} 50%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
            color: "#fff",
          }}
        >
          Save Role
        </Button>
      </DialogActions>
    </Dialog>
  );
}
